<template>
  <form @submit.prevent="handleFormSubmit" autocomplete="off" novalidate>
    <div class="form-group">
      <label for="name">
        Schedule Name
        <span v-if="veeErrors.first('name')" class="invalid">
          - Field is required
        </span>
      </label>
      <input
        type="text"
        name="name"
        class="form-control"
        v-model.trim="formData.name"
        v-validate="'required'"
        data-vv-as="schedule name"
        placeholder="Enter schedule name"
        required
      />
    </div>
    <div class="form-group schedule-type">
      <label class="css-control css-control-primary css-radio">
        <input
          type="radio"
          class="css-control-input"
          :class="!formData.type ? 'checked' : ''"
          name="type"
          :value="false"
          checked
          v-model="formData.type"
        />
        <span class="css-control-indicator"></span> Open 24/7
      </label>
      <label class="css-control css-control-primary css-radio">
        <input
          type="radio"
          class="css-control-input"
          :class="formData.type ? 'checked' : ''"
          name="type"
          :value="true"
          v-model="formData.type"
        />
        <span class="css-control-indicator"></span> Custom
      </label>
    </div>
    <template v-if="formData.type === true">
      <div class="days-selection form-group row mb-0">
        <div
          class="item__option"
          v-for="(item, index) in days"
          :key="`${item.name}-${index}`"
        >
          <label
            class="css-control css-control-primary css-control-lg css-checkbox"
          >
            <input
              type="checkbox"
              class="css-control-input"
              :class="item.isEnabled ? 'checked' : ''"
              :value="item"
              v-model="formData.ScheduleMeta"
              @change="() => handleDaySelected(item, index)"
            />
            <span class="css-control-indicator rounded-circle"></span>
            <span class="item__label">
              {{ item.shortname }}
            </span>
          </label>
          <div v-if="item.isEnabled" class="item__timerange">
            <div
              class="select-box item__starttime"
              tabindex="0"
              :ref="`${item.shortname}-starttime`"
            >
              <label>From</label>
              <div class="select-box__current" tabindex="0">
                <div class="select-box__value">
                  <input
                    type="text"
                    :name="`${item.shortname}-starttime`"
                    class="select-box__input"
                    :value="handleToTime(item.startTime)"
                    readonly="readonly"
                    @click="
                      handleToggleTimeDropdown(`${item.shortname}-starttime`)
                    "
                  />
                </div>
                <div
                  @click="
                    handleToggleTimeDropdown(`${item.shortname}-starttime`)
                  "
                >
                  <icon-base class="select-box__icon" iconName="More">
                    <IconArrowDown />
                  </icon-base>
                </div>
              </div>
              <Scrollbar
                class="scrollbar scrollbar--primary scrollbar--timeset"
                :style="{
                  '--primary': scheme.primary,
                  '--secondary': scheme.secondary
                }"
              >
                <ul class="select-box__list select-box__list--starttime">
                  <li
                    v-for="(input, i) in timesets"
                    :key="`${item.shortname}-starttime-option-${i}`"
                    :ref="`${item.shortname}-starttime__input`"
                  >
                    <label
                      class="select-box__option"
                      aria-hidden="aria-hidden"
                      @click="
                        handleTimeSelected(
                          item,
                          input,
                          'startTime',
                          `${item.shortname}-starttime`
                        )
                      "
                      tabindex="0"
                    >
                      {{ input | toTime }}
                    </label>
                  </li>
                </ul>
              </Scrollbar>
            </div>
            <div
              class="select-box item__endtime"
              :ref="`${item.shortname}-endtime`"
              tabindex="0"
            >
              <label for="endTime">To</label>
              <div class="select-box__current" tabindex="0">
                <div class="select-box__value">
                  <input
                    type="text"
                    :name="`${item.shortname}-endtime`"
                    class="select-box__input"
                    :value="handleToTime(item.endTime)"
                    readonly="readonly"
                    @click="
                      handleToggleTimeDropdown(`${item.shortname}-endtime`)
                    "
                  />
                </div>
                <div
                  @click="handleToggleTimeDropdown(`${item.shortname}-endtime`)"
                >
                  <icon-base class="select-box__icon" iconName="More">
                    <IconArrowDown />
                  </icon-base>
                </div>
              </div>
              <Scrollbar
                class="scrollbar scrollbar--primary scrollbar--timeset"
                :style="{
                  '--primary': scheme.primary,
                  '--secondary': scheme.secondary
                }"
              >
                <ul class="select-box__list select-box__list--endtime">
                  <li
                    v-for="(input, i) in timesets"
                    :key="`${item.shortname}-endtime-option-${i}`"
                    :ref="`${item.shortname}-endtime__input`"
                  >
                    <label
                      class="select-box__option"
                      aria-hidden="aria-hidden"
                      @click="
                        handleTimeSelected(
                          item,
                          input,
                          'endTime',
                          `${item.shortname}-endtime`
                        )
                      "
                      :class="{
                        disabled: !isTimeAfter(input, item.startTime)
                      }"
                    >
                      {{ input | toTime }}
                    </label>
                  </li>
                </ul>
              </Scrollbar>
            </div>
          </div>
        </div>
      </div>
      <div v-if="forCustomSetup" class="invalid-feedback d-block mb-2">
        Set atleast one day for custom Settings
      </div>
      <div class="form-group">
        <a
          class="btn-duplicate"
          href="javascript:;"
          :style="{ color: scheme.primary }"
          @click="handleCopyToAll"
          v-if="fetchCopySchedule"
        >
          <icon-base style="height: 1rem; width: 1rem; margin-top: -.25rem">
            <IconCopy />
          </icon-base>
          Copy to All
        </a>
      </div>
    </template>
    <div class="form-group text-right">
      <ActionButton
        class="d-inline-block mr-2 mt-0"
        mode="outline"
        label="Cancel"
        style="width: auto;"
        :theme="scheme"
        @customEvent="$emit('close')"
      />
      <ActionButton
        type="submit"
        class="d-inline-block mt-0"
        label="Save"
        style="width: auto;"
        :theme="scheme"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>

<script>
import {
  format as formatDate,
  isValid as isValidDate,
  isAfter
} from "date-fns";
import { mixin as clickaway } from "vue-clickaway";
import { mapActions, mapGetters } from "vuex";
import axios from "@/helpers/axios";
import { socketSender } from "@/helpers/socket";

const ActionButton = () => import("@/components/ActionButton");
const IconCopy = () => import("@/components/icons/IconCopy");
const IconArrowDown = () => import("@/components/icons/IconArrowDown");
const Scrollbar = () => import("@/components/Scrollbar");

const BASE_DATE = new Date(2001, 0, 1);
const START_OF_DAY = new Date(BASE_DATE.setHours(0, 0, 0, 0)).toISOString();
const END_OF_DAY = new Date(BASE_DATE.setHours(23, 59, 59, 999)).toISOString();

export default {
  name: "AddSchedule",
  components: {
    ActionButton,
    IconCopy,
    IconArrowDown,
    Scrollbar
  },
  props: {
    singleRecord: {
      type: Object,
      default: () => {}
    },
    link: {
      type: String,
      default: null
    }
  },
  mixins: [clickaway],
  filters: {
    toTime(val, label = "-") {
      if (!isValidDate(new Date(val))) return label;

      return formatDate(new Date(val), "h:mm A");
    }
  },
  data() {
    return {
      isLoading: true,
      days: [
        {
          name: "Monday",
          shortname: "Mon",
          isEnabled: false,
          startTime: START_OF_DAY,
          endTime: END_OF_DAY
        },
        {
          name: "Tuesday",
          shortname: "Tue",
          isEnabled: false,
          startTime: START_OF_DAY,
          endTime: END_OF_DAY
        },
        {
          name: "Wednesday",
          shortname: "Wed",
          isEnabled: false,
          startTime: START_OF_DAY,
          endTime: END_OF_DAY
        },
        {
          name: "Thursday",
          shortname: "Thu",
          isEnabled: false,
          startTime: START_OF_DAY,
          endTime: END_OF_DAY
        },
        {
          name: "Friday",
          shortname: "Fri",
          isEnabled: false,
          startTime: START_OF_DAY,
          endTime: END_OF_DAY
        },
        {
          name: "Saturday",
          shortname: "Sat",
          isEnabled: false,
          startTime: START_OF_DAY,
          endTime: END_OF_DAY
        },
        {
          name: "Sunday",
          shortname: "Sun",
          isEnabled: false,
          startTime: START_OF_DAY,
          endTime: END_OF_DAY
        }
      ],
      formData: {
        name: "",
        type: false,
        ScheduleMeta: []
      },
      editScheduleId: null,
      forCustomSetup: false
    };
  },
  computed: {
    ...mapGetters({
      fetchCopySchedule: "fetchCopySchedule"
    }),
    scheme() {
      return this.$store.getters.currentScheme;
    },
    timesets() {
      const dt = new Date(BASE_DATE.setHours(0, 0, 0, 0));
      const rc = [];
      const step = 30;

      while (dt.getDate() == 1) {
        rc.push(dt.toISOString());
        dt.setMinutes(dt.getMinutes() + step);
      }

      dt.setSeconds(dt.getMinutes() - 1);
      rc.push(dt.toISOString());

      return rc;
    }
  },
  mounted() {
    this.isLoading = false;

    document.addEventListener("click", this.handleToggleDropdownClose, false);
  },
  destroyed() {
    document.removeEventListener(
      "click",
      this.handleToggleDropdownClose,
      false
    );
  },
  created() {
    this.editSelectedSchedule();
    this.copyScheduleData(null);
  },
  methods: {
    ...mapActions({
      copyScheduleData: "copyScheduleData"
    }),
    editSelectedSchedule() {
      if (this.singleRecord) {
        let singleRecord = this.singleRecord;
        let form = this.formData;

        this.editScheduleId = singleRecord.id;

        Object.keys(form).forEach(el => {
          form[el] = singleRecord[el];
          if (el == "ScheduleMeta") {
            this.formData.ScheduleMeta = singleRecord[el];
            this.days.forEach((d, k) => {
              for (var i = 0; i <= singleRecord[el].length; i++) {
                let metaData = singleRecord[el][i];
                if (metaData && metaData.shortname == d.shortname) {
                  this.days[k].name = metaData.name;
                  this.days[k].shortname = metaData.shortname;
                  this.days[k].isEnabled = true;
                  this.days[k].startTime = metaData.startTime;
                  this.days[k].endTime = metaData.endTime;
                }
              }
            });
          }
        });
      }
    },
    async handleFormSubmit() {
      this.isLoading = true;
      try {
        const isValid = await this.$validator.validateAll();
        let resData = null;

        if (this.formData.type == false) {
          this.formData.ScheduleMeta = [];
        }

        if (this.formData.ScheduleMeta.length === 0 && this.formData.type) {
          this.forCustomSetup = true;
          return false;
        }

        if (!isValid) {
          throw {
            type: "warning",
            title: "Warning",
            error: new Error("Please fill the required fields.")
          };
        } else {
          if (this.editScheduleId) {
            let unmapped = [];
            this.days.forEach(day => {
              this.formData.ScheduleMeta.forEach((meta, metaKey) => {
                if (day.shortname == meta.shortname) {
                  this.formData.ScheduleMeta[metaKey] = day;
                }
              });
            });

            this.formData.ScheduleMeta.map(data => {
              if (data.isEnabled === false) {
                unmapped.push(data);
              }
            });

            if (unmapped.length) {
              this.formData.ScheduleMeta = this.formData.ScheduleMeta.filter(
                data => {
                  let unmappedData = "";
                  for (var i = 0; i < unmapped.length; i++) {
                    if (unmapped[i].shortname == data.shortname) {
                      unmappedData = data.shortname;
                    }
                  }
                  return data.shortname != unmappedData;
                }
              );
            }

            await axios.patch(
              "/schedules/" + this.editScheduleId,
              this.formData
            );
            this.$toastr.success("Update Success!", "Success");
            this.$emit("close");
          } else {
            await socketSender(
              "updateLog",
              `${this.$store.getters.getFullUser} added new Schedule named ${this.formData.name}`
            );
            resData = await axios
              .post("/schedules", this.formData)
              .then(res => res.data.data);
            this.$toastr.success("Added new Schedule", "Success");
            this.$emit("close");
          }
          this.$store
            .dispatch("FETCH_COLLECTION_PER_PAGE", this.link)
            .then(() => {
              if (!resData) return;
              const entries = [
                resData.id,
                ...this.$store.state.admin.newSchedules
              ];

              this.$store.dispatch("SET_SCHEDULE_NEW", entries);
            });
        }
      } catch (error) {
        const errType = error.type || "error";
        const errTitle = error.title || "Error";
        const errMsg = error.error.message || error.message;

        this.$toastr[errType](errMsg, errTitle);
      } finally {
        this.isLoading = false;
      }
    },
    isTimeAfter(next, prev) {
      if (!next) return false;
      if (!prev) return true;

      return isAfter(new Date(next), new Date(prev));
    },
    handleDaySelected(item) {
      item.isEnabled = !item.isEnabled;
    },
    handleTimeSelected(item, value, key, ref) {
      const el = this.$refs[ref][0];
      const nextAvailableTime = this.timesets.find(i =>
        this.isTimeAfter(i, value)
      );

      item[key] = value;

      if (!this.fetchCopySchedule) {
        this.copyScheduleData(item);
      }

      if (this.editScheduleId) {
        this.days.forEach((day, dayKey) => {
          if (day.shortname === item.shortname) {
            this.days[dayKey][key] = value;
          }
        });
      }

      if (
        (key !== "endTime" && !item.endTime) ||
        (item.endTime && this.isTimeAfter(value, item.endTime)) ||
        (key !== "endTime" && item.endTime == value)
      ) {
        item.endTime = nextAvailableTime;
      }

      el.classList.toggle("is-toggled");
    },
    handleToggleTimeDropdown(ref) {
      const el = this.$refs[ref][0];
      const nodeList = document.querySelectorAll(".select-box.is-toggled");

      let endTimeIndex = this.$refs[ref + "__input"].findIndex(e => {
        return !e.firstChild.classList.contains("disabled");
      });

      if (endTimeIndex > 0) {
        this.$refs[ref + "__input"][endTimeIndex - 1].scrollIntoView({
          block: "start"
        });
      }

      el.classList.add("is-toggled");
      Array.from(nodeList).forEach(i => i.classList.remove("is-toggled"));
    },
    handleToggleDropdownClose(e) {
      const isInsideTarget = e.path.find(path => {
        if (path == window || path == document) return false;
        if (path.classList.contains("select-box")) return true;
        return false;
      });

      if (isInsideTarget) return;

      const nodeList = document.querySelectorAll(".select-box.is-toggled");
      Array.from(nodeList).forEach(i => i.classList.remove("is-toggled"));
    },
    handleToTime(val, label) {
      if (val && isValidDate(new Date(val)))
        return this.$options.filters.toTime(val, label);

      val = new Date(0);
      val.setHours(0);
      val.setMinutes(0);
      val.setMilliseconds(0);
      return this.$options.filters.toTime(val, label);
    },
    handleCopyToAll() {
      if (!this.fetchCopySchedule) return;

      const { shortname, startTime, endTime } = this.fetchCopySchedule;

      this.formData.ScheduleMeta.forEach(i => {
        this.days.forEach(day => {
          if (day.shortname !== i.shortname) return;
          if (day.shortname === shortname) return;

          day.startTime = startTime;
          day.endTime = endTime;
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.schedule-type {
  display: flex;
  flex-direction: row;
  .css-radio {
    padding-left: 0;
  }
}
.css-radio {
  font-family: "Gotham Book", $headings-font-family;
}

.css-checkbox.css-control-lg
  .css-control-input
  ~ .css-control-indicator::after {
  line-height: 1.6;
}

.css-checkbox.css-control-lg .css-control-input ~ .css-control-indicator {
  margin-bottom: 0.25em;
  margin-right: 0;
}

.days-selection {
  margin-left: -0.3125em;
  margin-right: -0.3125em;

  .css-checkbox {
    margin-bottom: 0.5em;
  }
}

.btn-duplicate {
  font-family: "Gotham Book", $headings-font-family;
  font-size: 14px;
  padding: 0.25em;
  text-decoration: none;
}

.item {
  &__option {
    flex: 1;
    padding: 0 0.3125em;
  }

  &__label {
    display: block;
    font-family: "Gotham Book";
    font-size: 0.75rem;
    font-weight: 600;
    text-align: center;
  }
}

.select-box {
  display: block;
  font-family: "Gotham Book", $headings-font-family;
  margin: 0 auto 0.75em;
  font-size: 12px;
  position: relative;
  width: 100%;

  &.is-toggled {
    .select-box__list {
      opacity: 1;
      visibility: visible;
      z-index: 1;
    }

    .select-box__option {
      cursor: pointer;
    }

    .select-box__icon {
      transform: translateY(-50%) scale(0.6) rotate(180deg);
    }

    .scrollbar--timeset {
      height: auto;
      opacity: 1;
      visibility: visible;
      transform: translateY(0.16667rem);
    }
  }

  &__current {
    cursor: pointer;
    outline: none;
    position: relative;
  }

  &__icon {
    color: $gray-800;
    height: 100%;
    padding: 0 0.5em;
    position: absolute;
    right: -0.25em;
    top: 50%;
    transform: translateY(-50%) scale(0.6);
    transition: 0.2s ease;
    width: 2.5em;

    &.is-toggled {
      transform: translateY(-50%) scale(0.6) rotate(180deg);
    }
  }

  &__value {
    display: flex;
  }

  &__input {
    border: 1px solid $gray-350;
    border-radius: 0.35714em;
    font-family: "Gotham Book", $headings-font-family;
    margin: 0;
    padding: 0.83em 0.5em;
    width: 100%;

    &:hover {
      cursor: pointer;
    }
  }

  &__list {
    background-color: $white;
    list-style: none;
    margin: 0;
    opacity: 0;
    padding: 0;
    width: 100%;
    visibility: hidden;
    z-index: 1;

    li {
      display: block;
    }
  }

  &__option {
    background-color: $white;
    display: block;
    font-family: "Gotham Book", $headings-font-family;
    font-weight: 400;
    margin: 0;
    padding: 0.5em;

    &:hover,
    &:focus {
      background-color: $brand-secondary;
      background-color: var(--secondary, $brand-secondary);
      cursor: pointer;
    }

    &.disabled {
      background-color: transparentize(#000, 0.9);
      color: transparentize(#000, 0.6);
      pointer-events: none;
    }
  }
}

.scrollbar {
  &.scrollbar--timeset {
    height: 0;
    opacity: 0;
    position: absolute;
    transform: translateY(-2em);
    transition: opacity 250ms, visibility 250ms, transform 250ms;
    visibility: hidden;
    z-index: 1;
  }
}

.scrollbar--primary {
  box-shadow: 0px 1px 2px 0px transparentize(#000, 0.9),
    0px 2px 10px 0px transparentize(#000, 0.9);
  border-radius: 0.416667em;
  max-height: 16.25em;
  width: 100%;
}
</style>

<style lang="scss">
.days-selection .v-select {
  .dropdown-menu {
    max-height: 100px;
    max-width: 100px;
  }

  .vs__selected-options {
    padding-left: 0;
    padding-right: 0;
  }

  .vs__actions {
    padding-right: 0.25rem;
  }

  .selected-tag {
    margin-right: 0;
  }
}
</style>
